/* eslint-disable no-template-curly-in-string */
import { memo, useEffect, useMemo, useState } from "react";

import { useCart } from "@shopify/hydrogen-react";

import {
  type CartFreeShippingProgressBarContent,
  type CartFreeShippingProgressBarSettings,
} from "@tablescape/state";
import { formatMoney } from "@tablescape/utilities";

import { CartFreeShippingProgressBarView } from "./CartFreeShippingProgressBar.view";

type Props = CartFreeShippingProgressBarContent &
  CartFreeShippingProgressBarSettings & {
    progressBarVisible: boolean;
    totalAmount?: number;
  };

const CartFreeShippingProgressBarBase = ({
  progressBarVisible,
  thresholdInProgressText,
  thresholdReachedText,
  thresholdValue,
  totalAmount,
}: Props) => {
  const { cost } = useCart();
  const [progressPercentage, setProgressPercentage] = useState(0);

  const totalAmountInCart = useMemo(() => {
    if (totalAmount) {
      return totalAmount;
    }

    if (!cost?.subtotalAmount?.amount) {
      return 0;
    }

    return parseFloat(cost.subtotalAmount.amount);
  }, [cost?.subtotalAmount?.amount, totalAmount]);

  const currentAmount = useMemo(
    () => thresholdValue - totalAmountInCart,
    [thresholdValue, totalAmountInCart]
  );

  useEffect(() => {
    if (!progressBarVisible) {
      setProgressPercentage(0);
      return;
    }

    requestAnimationFrame(() => {
      setProgressPercentage(
        Math.min(totalAmountInCart / thresholdValue, 1) * 100
      );
    });
  }, [thresholdValue, progressBarVisible, totalAmountInCart]);

  const currencySuffix = useMemo(() => {
    switch (window.Shopify?.currency?.active) {
      case "AUD":
      case "CAD": {
        return window.Shopify.currency.active;
      }

      default: {
        return undefined;
      }
    }
  }, []);

  const hasReachedThreshold = useMemo(
    () => currentAmount <= 0,
    [currentAmount]
  );

  const progressText = useMemo(() => {
    if (!hasReachedThreshold) {
      return thresholdInProgressText.replace(
        "${x}",
        formatMoney(
          Math.round(currentAmount) * 100,
          "${{ amount_no_trailing_zeroes }}",
          currencySuffix
        )
      );
    }

    return thresholdReachedText;
  }, [
    currencySuffix,
    currentAmount,
    hasReachedThreshold,
    thresholdInProgressText,
    thresholdReachedText,
  ]);

  return (
    <CartFreeShippingProgressBarView
      hasReachedThreshold={hasReachedThreshold}
      progressPercentage={progressPercentage}
      progressText={progressText}
    />
  );
};

export const CartFreeShippingProgressBar = memo(
  CartFreeShippingProgressBarBase
);
